import { useCallback, useRef, useState } from 'react';
import { CopyIcon, Link2Icon, PauseIcon, PlayIcon, ShareIcon } from 'lucide-react';
import { app, xhs } from '@/lib/bridge';
import { useAppReady } from '@/lib/useAppReady';
import { useGroupedSurface } from '@/lib/useGroupedSurface';
import { AccountCard } from '@/components/AccountCard';
import { PageShell } from '@/components/PageShell';
import { PageSkeleton } from '@/components/skeletons';
import { TaskProgressCard } from '@/components/task-ui';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CARD, HINT, MUTED } from '@/components/ui';
import { sleep, ToolNotices, useXhsTool, xhsErrorText } from './common';

/**
 * 薯薯 · 导出小红书收藏链接（`/shushu/xhs/collect-export?accountId=`）。
 *
 * 移植自搬小书 `mobile/tools/collect-export`：按游标读线上收藏（`collect/page`，每页 30 条，间隔 1.5 秒），
 * 组装成笔记链接，复制或分享。只读不写。停下（手动 / 限流 / 风控）后已读到的照样能导出，
 * 「继续读取」从内存里的游标接着翻；不跨会话保存，重读成本低。
 */

const PAGE_DELAY_MS = 1500;

type CollectNote = {
  note_id?: string;
  display_title?: string;
  type?: string;
  xsec_token?: string;
  user?: { nickname?: string };
};
type CollectPage = { notes?: CollectNote[]; cursor?: string; has_more?: boolean };
type LinkItem = { noteId: string; title: string; author: string; type: string; url: string };
type Phase = 'idle' | 'fetching' | 'stopped' | 'done';

function toLinkItem(note: CollectNote): LinkItem | null {
  if (!note.note_id) return null;
  const base = `https://www.xiaohongshu.com/explore/${note.note_id}`;
  return {
    noteId: note.note_id,
    title: note.display_title?.trim() || '（无标题）',
    author: note.user?.nickname ?? '',
    type: note.type === 'video' ? '视频' : '图文',
    // 网页端打开笔记要带 xsec_token，不带经常被拦到登录页
    url: note.xsec_token ? `${base}?xsec_token=${encodeURIComponent(note.xsec_token)}&xsec_source=pc_collect` : base,
  };
}

export function ShushuXhsCollectExportPage() {
  const tool = useXhsTool();
  const { account, usable, loaded } = tool;
  const [items, setItems] = useState<LinkItem[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [phase, setPhase] = useState<Phase>('idle');
  const [error, setError] = useState('');
  const cursorRef = useRef('');
  const seenRef = useRef(new Set<string>());
  const stopRef = useRef(false);
  useGroupedSurface();
  useAppReady(loaded);

  const fetchAll = useCallback(
    async (resume: boolean) => {
      if (!account?.uid) return;
      stopRef.current = false;
      setPhase('fetching');
      setError('');
      if (!resume) {
        cursorRef.current = '';
        seenRef.current = new Set();
        setItems([]);
        // 线上收藏总数（selfinfo 的 tab_public.collectionNote.count），拿不到只影响进度显示
        try {
          const { data } = await xhs.request<{ tab_public?: { collectionNote?: { count?: unknown } } }>(account.id, {
            path: '/api/sns/web/v1/user/selfinfo',
          });
          const n = Number(data?.tab_public?.collectionNote?.count);
          setTotal(Number.isFinite(n) ? n : null);
        } catch {
          setTotal(null);
        }
      }
      for (;;) {
        if (stopRef.current) {
          setPhase('stopped');
          return;
        }
        try {
          const { data } = await xhs.request<CollectPage>(account.id, {
            path: '/api/sns/web/v2/note/collect/page',
            query: {
              num: 30,
              cursor: cursorRef.current,
              user_id: account.uid,
              image_formats: 'jpg,webp,avif',
              xsec_token: '',
              xsec_source: '',
            },
          });
          const fresh = (data?.notes ?? [])
            .map(toLinkItem)
            .filter((item): item is LinkItem => !!item && !seenRef.current.has(item.noteId));
          fresh.forEach((item) => seenRef.current.add(item.noteId));
          if (fresh.length) setItems((prev) => [...prev, ...fresh]);
          const next = data?.cursor ?? '';
          if (!data?.has_more || !next || next === cursorRef.current) {
            setPhase('done');
            void app.haptic('success');
            return;
          }
          cursorRef.current = next;
        } catch (e) {
          setError(xhsErrorText(e));
          setPhase('stopped');
          void app.haptic('error');
          return;
        }
        await sleep(PAGE_DELAY_MS);
      }
    },
    [account],
  );

  const linksText = (withTitle: boolean) =>
    items.map((i) => (withTitle ? `${i.title}${i.author ? ` - ${i.author}` : ''}\n${i.url}` : i.url)).join(withTitle ? '\n\n' : '\n');

  const copy = async () => {
    try {
      await app.writeClipboard(linksText(false));
      void app.toast(`已复制 ${items.length} 条链接`, 'success');
    } catch (e) {
      setError(xhsErrorText(e));
    }
  };

  const share = async () => {
    try {
      await app.share({ text: linksText(true) });
    } catch (e) {
      setError(xhsErrorText(e));
    }
  };

  const count = items.length;
  const fetching = phase === 'fetching';
  const complete = phase === 'done';
  const missing = complete && total ? Math.max(0, total - count) : 0;

  return (
    <PageShell title="导出收藏" description="把小红书收藏的笔记导出成链接">
      {!loaded ? <PageSkeleton kind="listTool" /> : null}
      <ToolNotices {...tool} />

      {loaded && account ? <AccountCard account={account} showId={false} /> : null}

      {loaded && usable ? (
        <>
          {phase === 'idle' ? (
            <Card className={CARD}>
              <p className={MUTED}>读取这个账号的全部收藏，导出每篇笔记的链接（含标题、作者）。只读取，不会修改任何数据。</p>
              <p className={HINT}>每 30 条约 1.5 秒，1000 条收藏约 1 分钟，读取时请保持页面打开。</p>
            </Card>
          ) : (
            <TaskProgressCard
              title={fetching ? '正在读取收藏' : complete ? '读取完成' : '已暂停'}
              right={complete ? `共 ${count} 条` : total === null ? `已读 ${count}` : `${count} / 约 ${Math.max(total, count)}`}
              ratio={complete ? 1 : total ? count / total : 0}
              finished={complete}
              paused={phase === 'stopped'}
              action={
                <>
                  {fetching ? (
                    <>
                      <p className={`mx-1 ${HINT}`}>请保持页面打开，退出会中断读取。总数是小红书显示的计数，为预估值。</p>
                      <Button variant="tinted" className="w-full" onClick={() => (stopRef.current = true)}>
                        <PauseIcon data-icon="inline-start" />
                        暂停
                      </Button>
                    </>
                  ) : null}
                  {phase === 'stopped' ? (
                    <p className={`mx-1 ${HINT}`}>{error || '已暂停'}。已读到的可以先导出，点「继续读取」接着读。</p>
                  ) : null}
                  {missing > 0 ? (
                    <p className={`mx-1 ${HINT}`}>
                      小红书显示 {total} 条，实际读到 {count} 条。差的 {missing} 条多为已删除、设为私密或无权查看的笔记，接口读不到，属正常情况，不是漏读。
                    </p>
                  ) : null}
                </>
              }
            />
          )}

          {!fetching && count > 0 ? (
            <div className="flex flex-col gap-2">
              <Button className="w-full" onClick={() => void share()}>
                <ShareIcon data-icon="inline-start" />
                分享（含标题和作者）
              </Button>
              <Button variant="tinted" className="w-full" onClick={() => void copy()}>
                <CopyIcon data-icon="inline-start" />
                复制全部链接
              </Button>
            </div>
          ) : null}

          {phase === 'stopped' ? (
            <Button variant="tinted" className="w-full" onClick={() => void fetchAll(true)}>
              <PlayIcon data-icon="inline-start" />
              继续读取
            </Button>
          ) : !fetching ? (
            <Button variant={complete ? 'tinted' : 'default'} className="w-full" onClick={() => void fetchAll(false)}>
              <Link2Icon data-icon="inline-start" />
              {complete ? '重新读取' : '开始读取收藏'}
            </Button>
          ) : null}
        </>
      ) : null}
    </PageShell>
  );
}
